import { CloudAlert, CloudUpload, Loader2 } from 'lucide-react';
import { useOfflineQueue } from '@/shared/hooks/useOfflineQueue';

/**
 * Badge shown in the header when offline mutations are pending sync.
 *
 * The count is read from the queue module through `useSyncExternalStore`, so it
 * tracks every enqueue live — it used to be React state the queue never
 * refreshed, which froze the badge at whatever it read at mount.
 *
 * A second badge appears when the queue has dead-lettered something: a write
 * that can never be replayed is silent data loss, and the destructive toast
 * that reports it is transient, so the failure needs a standing surface too.
 * That badge is a button — it names the evicted writes in its tooltip and
 * clears the dead-letter store when clicked. Without it the only way out of a
 * standing red "N failed" was to sign out, which is not a dismissal, it is a
 * workaround.
 */
export function PendingSyncBadge() {
  const { pendingCount, failedMutations, isSyncing, dismissFailed } =
    useOfflineQueue();
  const failedCount = failedMutations.length;

  if (pendingCount === 0 && failedCount === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      {pendingCount > 0 && (
        <div
          className="flex items-center gap-1.5 rounded-md bg-warning/10 px-2.5 py-1.5 text-xs font-medium text-warning"
          role="status"
          aria-live="polite"
          aria-label={`${pendingCount} pending change${pendingCount > 1 ? 's' : ''} waiting to sync`}
        >
          {isSyncing ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <CloudUpload className="size-3.5" aria-hidden="true" />
          )}
          <span>{pendingCount} pending</span>
        </div>
      )}
      {failedCount > 0 && (
        <button
          type="button"
          onClick={() => {
            void dismissFailed();
          }}
          className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/20"
          title={failedMutations
            .map((record) => `${record.mutation.description}: ${record.reason}`)
            .join('\n')}
          aria-label={`${failedCount} change${failedCount > 1 ? 's' : ''} could not be saved — dismiss`}
        >
          <CloudAlert className="size-3.5" aria-hidden="true" />
          <span>{failedCount} failed</span>
        </button>
      )}
    </div>
  );
}
